import { useIsMutating } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';

import CheckmarkSmallSvg from '@/components/common/CheckmarkSmallSvg';
import LoadingSvg from '@/components/common/LoadingSvg';
import { Dialog } from '@/components/shopping_bag/Dialog';
import { useAuthenticated } from '@/components/shopping_bag/hooks/useAuthenticated';
import { Button } from '@/components/ui/button';
import { TmCreditCardType, TmCreditCard } from '@/tscapi/model/account';

import { usePayment } from '../Context/PaymentContext';
import { useAccount } from '../hooks/useAccount';
import { useRemoveCreditCard } from '../hooks/useCreditcard';
import { getLogoByCreditCardTypeNew, cardTypeName } from '../utils/payment.utils';

import { CardTypeSelector } from './CardTypeSelector';

export const ManagePayment = () => {
    const {
        accordionStates,
        setAccordionStates,
        handleAccordionStateChange,
        setShowNotification,
        setShowWarning,
        mode,
        setMode,
        updateAccordionAtIndex,
    } = usePayment();
    const isMutating = useIsMutating();
    const isAuthenticated = useAuthenticated();
    const { data, isFetching, error } = useAccount(isAuthenticated);
    const mutation = useRemoveCreditCard();
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [selectedCardType, setSelectedCardType] = useState(null);
    const [selectedCreditCard, setSelectedCreditCard] = useState<TmCreditCard>(null);
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);
    const [currentItemId, setCurrentItemId] = useState(0);

    const toggleDialog = () => {
        setShowRemoveDialog(!showRemoveDialog);
    };

    // Custom sorting function
    function customSort(a, b) {
        if (a.IsDefault && !b.IsDefault) {
            return -1; // `a` comes before `b`
        }
        if (!a.IsDefault && b.IsDefault) {
            return 1; // `b` comes before `a`
        }
        return 0; // No change in ordering
    }

    return (
        <>
            {mode == 'view' ? (
                <>
                    {/* view existing cards */}
                    {data && (
                        <div className="grid grid-cols-1 gap-5 md2:grid-cols-2">
                            {data.CreditCards &&
                                data.CreditCards.length > 0 &&
                                data.CreditCards.sort(customSort).map((card, index) => {
                                    //render cccards
                                    return (
                                        <div className="" key={index}>
                                            <div className="mt-1 flex flex-col flex-wrap border-4 border-solid border-[#d1dde2] bg-white px-5 py-5 text-[13px] font-light">
                                                <div className="mb-4 h-8 max-h-8 " data-qm-block="true">
                                                    {getLogoByCreditCardTypeNew(card.Type)}{' '}
                                                </div>
                                                <div>
                                                    {cardTypeName(card.Type)} ending in: {card.Number.substr(12, 4)}
                                                </div>
                                                <div className="manage__expiry" data-qm-block="true">
                                                    Expires on: {card.DisplayExpirationMonth}/
                                                    {card.DisplayExpirationYear.slice(2, 4)}
                                                </div>

                                                <div className="manage__default">
                                                    {card.IsDefault && (
                                                        <>
                                                            <CheckmarkSmallSvg /> Default payment card
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="mt-2.5 grid grid-cols-2 gap-2 ">
                                                <Button
                                                    tabIndex={0}
                                                    aria-label={'Edit'}
                                                    size="lg"
                                                    variant="outline"
                                                    onClick={() => {
                                                        updateAccordionAtIndex(0, true);
                                                        updateAccordionAtIndex(1, false);
                                                        setMode('edit');
                                                        setSelectedCardId(card.Id);
                                                        setSelectedCardType(card.Type);
                                                        setSelectedCreditCard(card);
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    tabIndex={0}
                                                    aria-label={'Remove'}
                                                    size="lg"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setCurrentItemId(card.Id);
                                                        setShowRemoveDialog(true);
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </>
            ) : (
                <>
                    {/* edit a specific card */}
                    <CardTypeSelector
                        selectedCardId={selectedCardId}
                        selectedCardType={selectedCardType}
                        selectedCreditCard={selectedCreditCard}
                    />
                </>
            )}
            {showRemoveDialog && (
                <>
                    <Dialog
                        title={'Are you sure you want to remove this card?'}
                        containerCss="removeDialog"
                        height="auto"
                        onCloseClick={() => {
                            setShowRemoveDialog(!showRemoveDialog);
                        }}
                    >
                        <p>
                            All associated information will be removed from your profile except any orders placed with
                            this credit card.
                        </p>
                        <div className="dialog__buttons--container" key={'dialog-button' + currentItemId}>
                            <button
                                type="button"
                                aria-label={'Yes, remove this card'}
                                className="dialog__buttons remove"
                                onClick={() => {
                                    mutation.mutate(currentItemId, {
                                        onSuccess: () => {
                                            setShowNotification(false);
                                            setShowWarning(true);
                                        },
                                    });
                                    setShowRemoveDialog(!showRemoveDialog);
                                }}
                            >
                                {'Yes, remove this card'}
                            </button>
                            <button
                                type="button"
                                aria-label={'Cancel remove credit card'}
                                className="dialog__buttons cancel"
                                onClick={() => {
                                    setShowRemoveDialog(!showRemoveDialog);
                                }}
                            >
                                No, keep this card
                            </button>
                        </div>
                    </Dialog>
                </>
            )}
            {mutation.isLoading || isMutating ? (
                <div className="blockPageWrap" style={{ display: 'block' }}>
                    <div className="blockPageOverlay"></div>
                    <div className="blockMsg">
                        <LoadingSvg />
                    </div>
                </div>
            ) : null}
        </>
    );
};
